import type { TimelineEventViewModel } from '@/api/types/TimelineEventViewModel';

import { useBoolean } from 'minimal-shared/hooks';

import Timeline from '@mui/lab/Timeline';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import { Box, Button, Divider, Typography } from '@mui/material';

import {
  formatDate,
  mapEventColorToMui,
} from 'src/sections/employer-data/shared/utils/policyUtils';
import {
  StyledTitle,
  StyledDescription,
  StyledTimelineItem,
  StyledTimelineContent,
} from 'src/sections/employer-data/shared/styles/sharedStyles';

const DEFAULT_DISPLAY_COUNT = 4;

type TimelineProps = {
  title: string;
  timelineEvents: TimelineEventViewModel[];
};

export default function SharedTimeline({ title, timelineEvents }: TimelineProps) {
  const showAll = useBoolean(false);

  const displayCount = showAll.value ? timelineEvents.length : DEFAULT_DISPLAY_COUNT;

  return (
    <Box sx={{ pb: '24px' }}>
      <Typography
        variant="h6"
        sx={{ color: 'text.primary', padding: '1.5rem' }}
        data-testid="timeline-profile-title"
      >
        {title}
      </Typography>
      <Timeline sx={{ padding: 0, maxWidth: '100%' }}>
        {timelineEvents.map((event, index) =>
          index < displayCount || index === timelineEvents.length - 1 ? (
            <StyledTimelineItem
              key={event.title + '-' + index}
              color={mapEventColorToMui(event.timelineEvent)}
            >
              <TimelineSeparator>
                <TimelineDot color={mapEventColorToMui(event.timelineEvent)} />
                {index < timelineEvents.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <StyledTimelineContent>
                <StyledTitle
                  variant="subtitle2"
                  data-testid={`timeline-title-${event?.title?.replace(/\s+/g, '-').toLowerCase()}-${index}`}
                >
                  {event.title || ''}
                </StyledTitle>

                {event.description && (
                  <StyledDescription
                    variant="body2"
                    data-testid={`timeline-description-${event?.title?.replace(/\s+/g, '-').toLowerCase()}-${index}`}
                  >
                    {event.description || ''}
                  </StyledDescription>
                )}
                <StyledDescription
                  variant="caption"
                  data-testid={`timeline-date-${event?.title?.replace(/\s+/g, '-').toLowerCase()}-${index}`}
                >
                  {formatDate(event.eventDate)}
                </StyledDescription>
              </StyledTimelineContent>
            </StyledTimelineItem>
          ) : (
            index === displayCount && (
              <StyledTimelineItem key={event.title} sx={{ mt: '12px' }}>
                <TimelineSeparator sx={{ minWidth: '12px', alignItems: 'center' }}>
                  {index < timelineEvents.length - 1 && (
                    <Divider
                      sx={{
                        height: '4.4375rem',
                        width: '0.0625rem',
                        borderStyle: 'dashed',
                        borderWidth: '0.0625rem',
                      }}
                    />
                  )}
                </TimelineSeparator>
                <StyledTimelineContent
                  sx={{
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Button onClick={showAll.onToggle} sx={{ color: 'text.secondary', ml: -1 }}>
                    {showAll.value ? 'See less' : 'See all updates'}
                  </Button>
                </StyledTimelineContent>
              </StyledTimelineItem>
            )
          )
        )}
      </Timeline>
    </Box>
  );
}
